/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { ContactDeliveryMethod } from '@gh/builder-shared-data';
import { ContextRoute, getContextRouteInitData } from '@gh/common-internal-shared';
import { EnumDictionary } from '@gh/common-shared';
import { assign } from 'lodash';
import { ContactDetailsDebtorCommonFormState } from './contact-details-debtor-common-form.service';
import { AddressAutocompleteComponent, AddressComponents } from '@gh/core-ui';

@Component({
  standalone: false,
  selector: 'gh-contact-details-debtor-common-tab',
  templateUrl: 'contact-details-debtor-common-tab.component.html',
})
export class ContactDetailsDebtorCommonTabComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent | undefined;

  deliveryMethods: EnumDictionary<ContactDeliveryMethod>;
  showManualAddressFields = false;
  private checkInterval: any;

  constructor(private route: ContextRoute,
    public form: ContactDetailsDebtorCommonFormState) {
  }

  ngOnInit(): void {
    assign(this, getContextRouteInitData(this.route));
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.onAddressChange();

      const billingForm = this.form.group.get('billing');
      if (billingForm) {
        const hasAddressData = !!(billingForm.get('street1')?.value ||
          billingForm.get('street2')?.value ||
          billingForm.get('city')?.value ||
          billingForm.get('state')?.value ||
          billingForm.get('country')?.value ||
          billingForm.get('postcode')?.value);
        this.showManualAddressFields = hasAddressData;
      }
    });

    this.checkInterval = setInterval(() => {
      this.checkForAddressFieldErrors();
    }, 200);
  }

  ngOnDestroy(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }

  private checkForAddressFieldErrors(): void {
    if (this.showManualAddressFields) {
      return;
    }

    const billingForm = this.form.group.get('billing');
    if (!billingForm) return;

    const addressFields = ['street1', 'street2', 'city', 'state', 'country', 'postcode'];
    const hasErrors = addressFields.some(field => {
      const control = billingForm.get(field);
      return control && control.invalid && control.touched;
    });

    if (hasErrors) {
      this.showManualAddressFields = true;
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    }
  }

  onUpdateBilling(): void {
    this.form.updateBilling();
  }

  onAddressChange() {
    const commonDetailsForm = this.form.group.get('billing');
    if (!commonDetailsForm) return;

    const street1 = commonDetailsForm.get('street1')?.value || '';
    const street2 = commonDetailsForm.get('street2')?.value || '';
    const city = commonDetailsForm.get('city')?.value || '';
    const state = commonDetailsForm.get('state')?.value || '';
    const postcode = commonDetailsForm.get('postcode')?.value || '';
    const country = commonDetailsForm.get('country')?.value || '';

    const addressParts: string[] = [];

    const streetCombined = [street2, street1].filter(Boolean).join(' ');
    if (streetCombined) addressParts.push(streetCombined);
    if (city) addressParts.push(city);
    const stateCombined = [state, postcode].filter(Boolean).join(' ');
    if (stateCombined) addressParts.push(stateCombined);
    if (country) addressParts.push(country);

    const newAddress = addressParts.join(', ');

    if (this.addressAutocomplete) {
      this.addressAutocomplete.inputValue = newAddress;
    }
  }

  onAddressSelected(components: AddressComponents): void {
    const commonDetailsForm = this.form.group.get('billing');
    if (!commonDetailsForm) return;

    const streetParts: string[] = [];
    if (components.streetNo) streetParts.push(components.streetNo);
    if (components.street) streetParts.push(components.street);
    const street1Value = streetParts.join(' ').trim();

    commonDetailsForm.patchValue({
      street1: street1Value || '',
      city: components.city || '',
      state: components.stateName || '',
      stateCode: components.state || '',
      postcode: components.postalCode || '',
      country: components.country || ''
    });

    ['street1', 'city', 'state', 'stateCode', 'postcode', 'country'].forEach(field => {
      const control = commonDetailsForm.get(field);
      if (control) control.markAsDirty();
    });
  }
}
